<template>
  <b-container>
    <ResourceList :can-delete="hasAnyPermission(['delete-supply-order'])" :data="data"
                  :has-buttons="false" :has-filters="true" :is-deleting="isDeleting"
                  :is-loading="isLoading" :pagination="pagination" :search-query="$route.query.query"
                  create-text="Supply Order"
                  create-to="dash.supply_orders.create" resource="supply order" resource-plural="supply orders"
                  :title="showArchived ? 'Archived Supply Orders' : 'Supply Orders'"
                  @paginate="onPaginate" @search="onSearch" @delete-many="onDeleteMany">
      <template #selectedActions="{ selected }">
        <b-button v-if="!showArchived && selected.length && hasAnyPermission(['delete-supply-order'])"
                  class="mr-2 text-nowrap"
                  variant="outline-danger"
                  :disabled="isDeleting"
                  @click="confirmArchiveMany(selected)">
          <b-icon icon="archive"/>
          Archive {{ selected.length }}
        </b-button>
      </template>
      <template #filters>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="View" class="mb-3 mb-md-0">
              <b-form-select v-model="showArchived" @input="doPaginate">
                <b-form-select-option :value="false">Active</b-form-select-option>
                <b-form-select-option :value="true">Archived</b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Status" class="mb-0">
              <b-form-select v-model="status" @input="doPaginate">
                <b-form-select-option :value="null">Please select a status</b-form-select-option>
                <b-form-select-option value="Goods In">Goods In</b-form-select-option>
                <b-form-select-option value="Partial Goods In">Partial Goods In</b-form-select-option>
                <b-form-select-option value="Ordered">Ordered</b-form-select-option>
                <b-form-select-option value="Not Ordered">Not Ordered</b-form-select-option>
                <b-form-select-option value="Faulted">Faulted</b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
      </template>

      <template #listItem="{ item }">
        <div class="d-flex flex-column flex-fill">
          <div class="d-flex flex-row flex-fill">
            <p class="h5 flex-fill mb-0 text-primary">
              <router-link :to="{ name: 'dash.supply_orders.view', params: { id: item.id } }" class="text-dark">
                #{{ item.id }} {{ item.name }}
              </router-link>
            </p>
            <p class="align-self-end mb-0 text-muted">Created:
              <FriendlyDate :date="item.created_at"/>
            </p>
          </div>
          <div class="d-flex flex-row flex-fill mt-2">
            <b-button v-b-popover.hover.bottom="'View more information about this supply order.'"
                      :to="{ name: 'dash.supply_orders.view', params: { id: item.id } }"
                      class="mr-1 mb-0"
                      size="sm"
                      variant="primary">
              <b-icon icon="eye"/>
              View
            </b-button>
            <b-button v-if="hasAnyPermission(['update-supply-order'])"
                      v-b-popover.hover.bottom="'Edit this supply order'"
                      :to="{ name: 'dash.supply_orders.update', params: { id: item.id } }" class="mr-1 mb-0"
                      size="sm"
                      variant="secondary">
              <b-icon icon="pen"/>
              Edit
            </b-button>
            <b-button v-if="!showArchived && hasAnyPermission(['delete-supply-order'])"
                      v-b-popover.hover.bottom="'Archive this supply order'"
                      class="mr-1 mb-0"
                      size="sm"
                      variant="outline-danger"
                      :disabled="isDeleting"
                      @click="confirmArchive(item)">
              <b-icon icon="archive"/>
              Archive
            </b-button>
          </div>
        </div>
      </template>
    </ResourceList>
    <b-modal id="confirmArchiveModal" title="Archive Supply Order" hide-footer>
      <p v-if="archiveIds.length > 1">
        Archive {{ archiveIds.length }} supply orders? They will be hidden from the active list and can be opened from Archived.
      </p>
      <p v-else>
        Archive supply order #{{ archiveTarget && archiveTarget.id }}? It will be hidden from the active list and can be opened from Archived.
      </p>
      <div class="d-flex justify-content-end">
        <b-button class="mr-2" variant="light" @click="$bvModal.hide('confirmArchiveModal')">Cancel</b-button>
        <b-button variant="danger" :disabled="isDeleting" @click="doArchive">
          <b-spinner v-if="isDeleting" small></b-spinner>
          <span v-if="!isDeleting">Archive</span>
        </b-button>
      </div>
    </b-modal>
  </b-container>
</template>
<script>
import ResourceList from "../../components/ResourceList"
import currentUser from "../../mixins/currentUser"
import {mapActions, mapGetters} from "vuex"
import FriendlyDate from "../../components/FriendlyDate"

export default {
  mixins: [currentUser],
  created() {
    this.showArchived = this.$route.query.archived === 'true' || this.$route.query.archived === true
    this.status = this.$route.query.status || null
    this.fetchMany(this.listQuery())
  },
  components: {
    ResourceList,
    FriendlyDate
  },
  data: () => ({
    status: null,
    showArchived: false,
    archiveTarget: null,
    archiveIds: []
  }),
  computed: {
    ...mapGetters("authentication", ["hasAnyPermission"]),
    ...mapGetters("supply-orders", ["data", "isDeleting", "isLoading", "pagination"])
  },
  methods: {
    ...mapActions("supply-orders", ["archive", "batchArchive", "deleteMany", "fetchMany"]),

    listQuery(overrides = {}) {
      return {
        ...this.$route.query,
        status: this.status,
        archived: this.showArchived,
        ...overrides
      }
    },
    confirmArchive(item) {
      this.archiveTarget = item
      this.archiveIds = [item.id]
      this.$bvModal.show('confirmArchiveModal')
    },
    confirmArchiveMany(ids) {
      this.archiveTarget = null
      this.archiveIds = ids.slice()
      this.$bvModal.show('confirmArchiveModal')
    },
    async doArchive() {
      if (!this.archiveIds.length) {
        return
      }
      if (this.archiveIds.length === 1) {
        await this.archive(this.archiveIds[0])
      } else {
        await this.batchArchive({ ids: this.archiveIds })
      }
      this.$bvModal.hide('confirmArchiveModal')
      this.archiveTarget = null
      this.archiveIds = []
      await this.fetchMany(this.listQuery())
    },
    async onDeleteMany({ids, modalId}) {
      await this.deleteMany({ids})
      this.$bvModal.hide(modalId)
      await this.fetchMany(this.listQuery())
    },
    async doPaginate() {
      await this.$router.replace({
        name: this.$route.name,
        query: this.listQuery({ page: this.$route.query.page || 1 })
      }).catch(() => {
      })
      await this.fetchMany(this.listQuery())
    },

    async onPaginate(page) {
      if (this.$route.query.page !== page) {
        await this.$router.replace({
          name: this.$route.name,
          query: this.listQuery({ page })
        }).catch(() => {
        });
        await this.fetchMany(this.listQuery({ page })).catch(() => {
        });
      }
    },
    async onSearch(query) {
      if (this.$route.query.page !== query) {
        const path = {name: this.$route.name, query: this.listQuery({ query })};
        await this.$router.push(path).catch(() => {
        });
      }
      await this.fetchMany(this.listQuery({ query })).catch(() => {
      })
    },

  }
}
</script>
<style>
</style>
